import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import PaymentTermsDialog from "./PaymentTermsDialog";
import useProfessionalAccountStatus from "../../../hooks/useProfessionalAccountStatus";

import { DASHBOARD_PATH } from "../../../utils/paymentsRoutes";

/**
 * Makes agreeing to the terms the first step of registering an account.
 *
 * The registration screens are normally reached through the button on the
 * configuration list, which asks for the terms before navigating. This covers
 * the other way in — a bookmark or a pasted link — so the step cannot be
 * skipped.
 *
 * Closing leaves the payments area entirely rather than going back to the
 * configuration list. A professional who declines has no account and cannot
 * make one, and the list of nothing sends them to the welcome screen, which
 * asks for the terms again — the way back has to leave the loop.
 */
export function withRegistrationTerms<P extends object>(
  Component: React.ComponentType<P>
) {
  return function Gated(props: P) {
    return (
      <RegistrationTermsGate>
        <Component {...props} />
      </RegistrationTermsGate>
    );
  };
}

export default function RegistrationTermsGate({
  children
}: {
  children: React.ReactNode;
}) {
  const history = useHistory();
  const { mustAgreeToPaymentTerms, loading } = useProfessionalAccountStatus();
  const [accepted, setAccepted] = useState(false);

  const onClose = useCallback(() => history.push(DASHBOARD_PATH), [history]);

  if (loading || !mustAgreeToPaymentTerms || accepted) return <>{children}</>;

  return (
    <PaymentTermsDialog
      open
      onClose={onClose}
      onAccepted={() => setAccepted(true)}
    />
  );
}
